import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import {
  DEFAULT_CURRENCY,
  formatMoney,
  getCurrencySymbol,
  resolveCurrency,
} from '@/utils/currency';

export function useCurrency() {
  const { user } = useAuth();
  const { i18n } = useTranslation();

  const currency = resolveCurrency(user?.organization?.currency);
  const locale = i18n.language || 'es-ES';

  return useMemo(
    () => ({
      currency,
      locale,
      symbol: getCurrencySymbol(currency, locale),
      format: (value: number | string | null | undefined) =>
        formatMoney(value, { currency, locale }),
      formatRounded: (value: number | string | null | undefined) => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        const safeNum = num == null || Number.isNaN(num) ? 0 : num;
        return formatMoney(Math.round(safeNum), {
          currency,
          locale,
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        });
      },
      formatViewAmount: (
        value: number | string | null | undefined,
        viewType: 'units' | 'value',
      ) => {
        if (viewType === 'value') {
          const num = typeof value === 'string' ? parseFloat(value) : value;
          const safeNum = num == null || Number.isNaN(num) ? 0 : num;
          return formatMoney(Math.round(safeNum), {
            currency,
            locale,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
          });
        }
        const num = typeof value === 'string' ? parseFloat(value) : value;
        if (num == null || Number.isNaN(num)) return '0';
        return num.toLocaleString(locale, { maximumFractionDigits: 0 });
      },
      valueSuffix: (viewType: 'units' | 'value') =>
        viewType === 'value' ? ` ${getCurrencySymbol(currency, locale)}` : '',
    }),
    [currency, locale],
  );
}

export { DEFAULT_CURRENCY };
